import { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';

function normalizeBreweryIds(breweryIdOrIds) {
  if (Array.isArray(breweryIdOrIds)) {
    return breweryIdOrIds
      .filter((breweryId) => typeof breweryId === 'string' && breweryId.trim().length > 0)
      .map((breweryId) => breweryId.trim());
  }
  const singleId = typeof breweryIdOrIds === 'string' ? breweryIdOrIds.trim() : '';
  return singleId.length > 0 ? [singleId] : [];
}

async function fetchBeersForBreweryId(breweryId) {
  const collRef = collection(db, 'beer');
  let snap = null;
  try {
    const qry = query(
      collRef,
      where('availableBreweryIds', 'array-contains', breweryId),
      orderBy('name', 'asc'),
    );
    snap = await getDocs(qry);
  } catch {
    const qry = query(collRef, where('availableBreweryIds', 'array-contains', breweryId));
    snap = await getDocs(qry);
  }

  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
}

function breweryIdsDependencyKey(breweryIdOrIds) {
  return normalizeBreweryIds(breweryIdOrIds).join('\u0000');
}

/**
 * useBreweryBeers
 * Fetches beers filtered by one brewery id or a list of brewery ids.
 */
function useBreweryBeers(breweryIdOrIds) {
  const breweryIdsKey = breweryIdsDependencyKey(breweryIdOrIds);
  const breweryIds = useMemo(
    () => (breweryIdsKey.length > 0 ? breweryIdsKey.split('\u0000') : []),
    [breweryIdsKey],
  );
  const [beers, setBeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (breweryIds.length === 0) {
      setBeers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const beerLists = await Promise.all(
        breweryIds.map((breweryId) => fetchBeersForBreweryId(breweryId)),
      );

      const beersById = new Map();
      beerLists.forEach((beerList) => {
        beerList.forEach((beerRow) => {
          if (!beersById.has(beerRow.id)) {
            beersById.set(beerRow.id, beerRow);
          }
        });
      });

      const rows = Array.from(beersById.values());
      rows.sort((left, right) => {
        const nameLeft = (left.name || '').toLowerCase();
        const nameRight = (right.name || '').toLowerCase();
        return nameLeft.localeCompare(nameRight);
      });

      setBeers(rows);
    } catch (err) {
      console.error('Error fetching beers:', err);
      setError('Failed to load beers');
      setBeers([]);
    } finally {
      setLoading(false);
    }
  }, [breweryIds]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { beers, loading, error, refresh };
}

export default useBreweryBeers;

